import * as api from '../../api';
import { Asset, LoadAssetsFilter, LoadSharingAssetsReturn, SharingLink } from '../../api';
import * as sharingActions from './actions';
import { message } from '@myua/kit';
import {
  getData,
  getScrollHash,
  getShareInProgress,
  getSharingId,
  getIsReplacingPack,
} from './selectors';
import { showSuccessNotification } from './components/SuccessNotification';
import { ShareContext } from './model';
import { getSortingFieldKey, getSortingOrder } from '../sorting/selectors';
import { getAssetsFilter } from '../app/selectors';
import { loadWithPagination } from '../finder/listeners';
import { EMPTY_TOTAL } from '../finder/model';
import { takeLatest, takeLatestById, takeEvery, ListenerApi } from '../../listenerMiddleware';
import { getSearchApiParams } from '../search/selectors';
import { routes, navigateToRoute } from '../router/model';
import * as packActions from '../pack/actions';

export const sharingListeners = () => {
  watchLoadAssetsRequested();
  watchShareAssetClicked();
  watchSharePackClicked();
  watchShareSharingClicked();
  watchEditClicked();
};

function watchLoadAssetsRequested() {
  takeLatest(sharingActions.loadDataRequested, async ({ payload }, listenerApi) => {
    const { getState } = listenerApi;

    const sharingId = getSharingId(getState());
    if (!sharingId) {
      return;
    }
    const { mode } = payload;

    const errorState: LoadSharingAssetsReturn = {
      assets: [],
      total: EMPTY_TOTAL,
      sharing_link: null,
    };

    const sortingOrder = getSortingOrder(getState());
    const sortingFieldKey = getSortingFieldKey(getState());
    const searchApiParams = getSearchApiParams(getState());

    await loadWithPagination(
      {
        mode,
        getData,
        getScrollHash,
        setLoading: sharingActions.setLoading,
        assetsChanged: sharingActions.assetsChanged,
        errorMessage: 'Assets have not been loaded',
        errorState,
        onRequest: (offset) =>
          api.loadSharingAssets({
            sharingId,
            orderBy: sortingFieldKey,
            ordering: sortingOrder,
            offset,
            ...searchApiParams,
          }),
      },
      listenerApi,
    );
  });
}

function watchShareAssetClicked() {
  takeEvery(sharingActions.shareAssetsClicked, async ({ payload }, listenerApi) => {
    const { getState } = listenerApi;

    const { assetIds, selectAll } = payload;

    // Если какой-то ассет уже в обработке, то выходим из саги.
    // UI, в принципе, не дает возникнуть такой ситуации.
    const inProgress = getShareInProgress(getState());
    if (
      inProgress[ShareContext.allAssets] ||
      assetIds?.some((id) => inProgress[ShareContext.asset][id])
    ) {
      return;
    }

    if (selectAll) {
      const setInProgress = (inProgress: boolean) =>
        sharingActions.setShareInProgress({ context: ShareContext.allAssets, inProgress });
      const filter = getAssetsFilter(getState());
      await share({ setInProgress, filter }, listenerApi);
    } else if (assetIds) {
      const setInProgress = (inProgress: boolean) =>
        sharingActions.setShareInProgress({ context: ShareContext.asset, inProgress, assetIds });
      await share({ setInProgress, assetIds }, listenerApi);
    }
  });
}

function watchSharePackClicked() {
  takeLatest(sharingActions.sharePackClicked, async (action, listenerApi) => {
    const setInProgress = (inProgress: boolean) =>
      sharingActions.setShareInProgress({
        context: ShareContext.pack,
        inProgress,
      });
    const messageFn = (copied: boolean) =>
      copied
        ? 'Pack has been saved as share link. Link copied to clipboard'
        : 'Pack has been saved as share link';
    await share({ setInProgress, messageFn }, listenerApi);
  });
}

function watchShareSharingClicked() {
  takeLatestById(
    sharingActions.shareSharingClicked,
    ({ payload }) => payload.sharingId,
    async ({ payload }, { dispatch }, { signal }) => {
      const { sharingId } = payload;
      const setInProgress = (inProgress: boolean) =>
        sharingActions.setShareInProgress({
          context: ShareContext.sharing,
          inProgress,
          sharingId,
        });
      await share({ setInProgress, sharingId }, { dispatch, signal });
    },
  );
}

async function share(
  {
    setInProgress,
    assetIds,
    sharingId,
    filter,
    messageFn,
  }: {
    setInProgress: (inProgress: boolean) => any;
    assetIds?: Asset['id'][];
    sharingId?: SharingLink['id'];
    filter?: LoadAssetsFilter;
    messageFn?: (copied: boolean) => string;
  },
  { dispatch, signal }: Pick<ListenerApi, 'dispatch' | 'signal'>,
) {
  dispatch(setInProgress(true));
  const result = await api.createSharing({ assetIds, sharingId, filter });
  if (signal.aborted) {
    return;
  }
  dispatch(setInProgress(false));

  if (result.success) {
    const sharingLink = result.data.response;
    void showSuccessNotification(
      sharingLink.id,
      messageFn ??
        ((copied) =>
          copied ? 'Shared link has been created and copied' : 'Shared link has been created'),
    );
  } else {
    void message.error('Sharing link has not been created');
  }
}

function watchEditClicked() {
  takeEvery(sharingActions.editClicked, async (action, { getState, dispatch }) => {
    const sharingLink = getData(getState()).sharing_link;
    const isReplacingPack = getIsReplacingPack(getState());
    if (!sharingLink || isReplacingPack) {
      return;
    }

    dispatch(sharingActions.setIsReplacingPack({ isReplacingPack: true }));
    const result = await api.replacePackWithSharing({ sharingId: sharingLink.id });
    dispatch(sharingActions.setIsReplacingPack({ isReplacingPack: false }));

    if (result.success) {
      dispatch(packActions.setRelatedSharing({ relatedSharing: sharingLink }));
      navigateToRoute(routes.pack);
    } else {
      void message.error('Pack has not been replaced');
    }
  });
}
